import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentChildren } from "preact";
import { cx } from "./lib/cx.ts";

export type ButtonVariant = "solid" | "quiet" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

type Shared = {
  /** `solid` is the primary action, `quiet` the default secondary, `ghost` a text-weight control, `danger` a text-only destructive action. Default `quiet`. */
  variant?: ButtonVariant | undefined;
  /** `sm` is 36px, `md` 40px. Default `md`. */
  size?: ButtonSize | undefined;
  /** A square button holding one icon; it must carry an `aria-label`. */
  icon?: boolean;
  class?: string | undefined;
  children?: ComponentChildren;
};
type AsButton = Shared & { href?: undefined } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "class" | "size" | "icon">;
// Preact 11's anchor attributes are a union on `href`; a link always has one, so it takes the variant whose roles fit a link.
type LinkAttributes = Exclude<AnchorHTMLAttributes<HTMLAnchorElement>, { href?: never }>;
type AsLink = Shared & { href: string } & Omit<LinkAttributes, "class" | "size" | "icon" | "href"> & { disabled?: boolean };
export type ButtonProps = AsButton | AsLink;

/**
 * A control that does something. Rule: one `solid` button per view at most;
 * everything else is `quiet`, and a destructive action is `danger` text, never
 * a filled red block. Renders an `<a>` when `href` is given, so navigation
 * stays a real link.
 */
export function Button(props: ButtonProps) {
  const { variant = "quiet", size = "md", icon, class: className, children, ...rest } = props;
  const classes = cx("ui-btn", `ui-btn--${variant}`, size === "sm" && "ui-btn--sm", icon && "ui-btn--icon", className);
  if (props.href !== undefined) {
    // SAFETY: a defined href means the caller passed AsLink props, so the rest are AsLink's own attributes.
    const { href, disabled, ...anchor } = rest as Omit<AsLink, keyof Shared>;
    return (
      <a {...anchor} href={href} class={classes} aria-disabled={disabled ? "true" : undefined}>
        {children}
      </a>
    );
  }
  // SAFETY: without an href the caller passed AsButton props, so the rest are button attributes.
  const button = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type="button" {...button} class={classes}>
      {children}
    </button>
  );
}

/**
 * The one edit affordance: the smallest quiet button, always reading "Edit",
 * placed in the row of the thing it edits. Rule: never an icon-only pencil.
 * `label` names what is edited ("Edit README") for assistive tech; the visible
 * word never changes.
 */
export function EditButton({ label, onClick, class: className }: { label: string; onClick?: () => void; class?: string }) {
  return (
    <Button variant="quiet" size="sm" aria-label={label} onClick={onClick} class={className}>
      Edit
    </Button>
  );
}
